import { css } from '@compiled/react';

import {
  Heading,
  Text,
  UnifiedAccountCardContent,
  UnifiedAccountTagLoggedIn,
  UnifiedAccountLinkAccountCard,
  UnifiedAccountTagLinked,
  UnifiedAccountTagPrimary,
  UnifiedAccountContainerCard,
} from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import SectionMessage from '@atlaskit/section-message';
import { Redirect, useQueryParam, useRouter } from 'react-resource-router';
import { commitMutation } from 'relay-runtime';
import { useLazyLoadQuery } from 'react-relay';
import { UnifiedAccountQuery } from 'graphql/__generated__/UnifiedAccountQuery.graphql';
import {
  InitializeTransactionMutation,
  InitializeTransactionMutation$variables,
} from 'graphql/__generated__/InitializeTransactionMutation.graphql';
import { environment } from 'services/relay';
import { INITIALIZE_TRANSACTION_MUTATION } from 'graphql/InitializeTransactionMutation';
import { UNLINK_MUTATION } from 'graphql/UnlinkMutation';
import { UnlinkMutation } from 'graphql/__generated__/UnlinkMutation.graphql';
import { UNIFIED_ACCOUNT_QUERY } from 'graphql/UnifiedAccountQuery';
import { linkedAccountDropdownActions } from '../../../utils/linkedAccountDropdownActions';
import { SetPrimaryAccountMutation } from 'graphql/__generated__/SetPrimaryAccountMutation.graphql';
import { SET_PRIMARY_ACCOUNT_MUTATION } from 'graphql/SetPrimaryAccountMutation';
import { ATLASSIAN_ACCOUNT_USER_QUERY } from 'constants/index';
import { AtlassianAccountUserQuery } from '~graphql/__generated__/AtlassianAccountUserQuery.graphql';
import { useEffect, useState } from 'react';
import FlagList from './flags/FlagList';
import { ALL_ACCOUNT_FLAGS_ERRORS } from '../../../constants/accountFlags';
import { Code, FlagDefinition } from './flags/FlagTypes';
import { MOBILE, DESKTOP } from '../../../constants/breakpoints';
import { truncateString } from '../../../utils/truncateString';
import { useSendScreenEventEffect } from 'hooks/useScreenEvent';
import {
  trackSettingsScreenView,
  trackLinkAccountButtonClick,
  trackAccountLinkingFailure,
  trackUnlinkAccountButtonClick,
  trackAccountUnlinkingSuccess,
  trackAccountUnlinkingFailure,
  trackMakePrimaryAccountButtonClick,
  trackMakePrimaryAccountSuccess,
  trackMakePrimaryAccountFailure,
} from 'analytics/settingsAnalytics';

const initializeTransaction = (
  input: InitializeTransactionMutation$variables = {},
): Promise<string> => {
  return new Promise((resolve, reject) => {
    commitMutation<InitializeTransactionMutation>(environment, {
      mutation: INITIALIZE_TRANSACTION_MUTATION,
      variables: input,
      onCompleted: (response, errors) => {
        const initializeTransactionVal =
          response?.unified?.linking?.initializeLinkingWithLoggedInAccount;

        if (initializeTransactionVal?.success === false || errors) {
          let errorCode = 'generic_error';
          if (errors) {
            console.error('Initialization of transaction failed', errors);
          } else {
            errorCode =
              initializeTransactionVal?.errors?.[0]?.code || 'generic_error';
          }
          reject({
            errorCode,
          });
        } else {
          if (!initializeTransactionVal?.token) {
            throw new Error('Token not found in response');
          }
          resolve(initializeTransactionVal?.token);
        }
      },
      onError: (error) => {
        console.error('Initialization of transaction failed', error);
        reject({
          errorCode: 'generic_error',
        });
      },
    });
  });
};

const unlink = (aaid: string) => {
  return new Promise((resolve, reject) => {
    commitMutation<UnlinkMutation>(environment, {
      mutation: UNLINK_MUTATION,
      variables: { aaid },
      onCompleted: (response, errors) => {
        const unlinkResponse = response?.unified?.account?.unlinkAccount;

        if (unlinkResponse?.success === false || errors) {
          let errorCode = 'generic_error';
          if (errors) {
            console.error('Initialization of transaction failed', errors);
          } else {
            errorCode = unlinkResponse?.errors?.[0]?.code || 'generic_error';
          }
          reject({
            errorCode,
          });
        } else {
          resolve(unlinkResponse?.success);
        }
      },
      onError: (error) => {
        console.error('Initialization of transaction failed', error);
        reject({
          errorCode: 'generic_error',
        });
      },
    });
  });
};

const setPrimaryAccount = (aaid: string) => {
  return new Promise((resolve, reject) => {
    commitMutation<SetPrimaryAccountMutation>(environment, {
      mutation: SET_PRIMARY_ACCOUNT_MUTATION,
      variables: { aaid },
      onCompleted: (response, errors) => {
        const accountResponse = response?.unified?.account?.setPrimaryAccount;
        if (accountResponse?.success === false || errors) {
          let errorCode = 'generic_error';
          if (errors) {
            console.error('Initialization of transaction failed', errors);
          } else {
            errorCode = accountResponse?.errors?.[0]?.code || 'generic_error';
          }
          reject({
            errorCode,
          });
        } else {
          console.log('Mutation successful', accountResponse?.message);
          resolve(accountResponse?.success);
          window.location.reload();
        }
      },
      onError: (error) => {
        console.error('Initialization of transaction failed', error);
        reject({
          errorCode: 'generic_error',
        });
      },
    });
  });
};

const { REACT_APP_IDENTITY_BASE_URL, REACT_APP_ACCOUNT_LINK_BASE_URL } =
  process.env;
const LinkComponent = ({
  loggedInAccountEmail,
  isLoggedInLinked,
  isLoggedInPrimary,
  onFailure,
}: {
  loggedInAccountEmail: string;
  isLoggedInLinked: boolean;
  isLoggedInPrimary: boolean;
  onFailure: (failureErrorCode: string) => void;
}) => {
  if (!isLoggedInPrimary && isLoggedInLinked) {
    return null;
  }
  const handleLinkAccountClick = async () => {
    trackLinkAccountButtonClick();

    try {
      const input: InitializeTransactionMutation$variables = {};
      const token = await initializeTransaction(input);

      const encryptedToken = encodeURIComponent(token); // this is very important, tokens can have / leading to unknown path.

      // https://atlassian.slack.com/archives/CFJ9DJ7P0/p1733439088988988559?thread_ts=1733403825.184989&cid=CFJ9DJ7P0
      const continueUrl = encodeURIComponent(
        `${REACT_APP_ACCOUNT_LINK_BASE_URL}/user/authenticate/${encryptedToken}`,
      );
      window.sessionStorage.setItem('sourceLinkingEmail', loggedInAccountEmail);
      const link = `${REACT_APP_IDENTITY_BASE_URL}/login?application=community--account-linking&sourceLinkingEmail=${loggedInAccountEmail}&prompt=login&continue=${continueUrl}`;

      window.open(link, '_self');
    } catch (e: any) {
      console.error('Failed to initialize linking', e);
      const errorCodeReceived = e?.errorCode || 'generic_error';

      trackAccountLinkingFailure(errorCodeReceived, 'initialization');
      onFailure(errorCodeReceived);
    }
  };
  return (
    <UnifiedAccountContainerCard
      onClick={handleLinkAccountClick}
      isAddAccount={true}
    >
      <UnifiedAccountLinkAccountCard />
    </UnifiedAccountContainerCard>
  );
};

const getTaggedComponents = (
  isPrimary: boolean,
  isLoggedIn: boolean,
  isLinked: boolean,
) => {
  const tags = [];
  if (isLoggedIn) {
    tags.push(<UnifiedAccountTagLoggedIn key="tag-logged-in" />);
  }
  if (isPrimary) {
    tags.push(<UnifiedAccountTagPrimary key="tag-primary" />);
  }
  if (isLinked && !isPrimary) {
    tags.push(<UnifiedAccountTagLinked key="tag-linked" />);
  }
  return tags;
};

const Settings = () => {
  const [, { replace }] = useRouter();
  const [errorCodeQueryParam] = useQueryParam('errorCode');
  const [errorCode, setErrorCode] = useState(errorCodeQueryParam);
  const [flags, setFlags] = useState<FlagDefinition[]>([]);

  useSendScreenEventEffect(() => trackSettingsScreenView());

  useEffect(() => {
    // clear the errorCode query parameter after consuming it. that way error doesn't persist.
    if (errorCodeQueryParam) {
      const url = new URL(window.location.href);
      url.searchParams.delete('errorCode');
      const newUrl = `${url.origin}${url.pathname}${url.search}`;
      replace(newUrl);
    }
  }, [errorCodeQueryParam, replace]);

  useEffect(() => {
    if (errorCode) {
      setFlags([
        {
          code: errorCode,
          component: [
            'invalid_primary_account_index',
            'invalid_primary_account',
            'invalid_non_primary_account',
          ].includes(errorCode)
            ? ALL_ACCOUNT_FLAGS_ERRORS['generic_account_link_error']
            : ALL_ACCOUNT_FLAGS_ERRORS['generic_error'],
        },
      ]);
    } else {
      setFlags([]);
    }
  }, [errorCode]);
  const handleLinkFailure = (failureErrorCode: string) => {
    setErrorCode(failureErrorCode);
  };

  const handleDismiss = (code: Code) => {
    setFlags((prevFlags) => prevFlags.filter((flag) => flag.code !== code));
    setErrorCode(undefined);
  };

  const loggedInUser = useLazyLoadQuery<AtlassianAccountUserQuery>(
    ATLASSIAN_ACCOUNT_USER_QUERY,
    {},
    { fetchPolicy: 'store-or-network' },
  )?.me?.user;
  if (!loggedInUser?.accountId) {
    return <Redirect to="/error/not_authorized" />;
  }
  const accountId = loggedInUser.accountId;

  const account = useLazyLoadQuery<UnifiedAccountQuery>(
    UNIFIED_ACCOUNT_QUERY,
    { aaid: accountId },
    { fetchPolicy: 'store-or-network' },
  )?.unified?.account;

  const isLoggedInAccountNotPrimary =
    account?.aaid === accountId && !account.isPrimary;

  const handleUnlink = async (aaid: string) => {
    trackUnlinkAccountButtonClick();

    try {
      const success = await unlink(aaid);
      if (success) {
        console.log('unlinked successfully', aaid);

        trackAccountUnlinkingSuccess();

        window.location.reload();
      } else {
        console.log('unlink failed', aaid);

        trackAccountUnlinkingFailure('unlink_returned_false');
      }
    } catch (e: any) {
      console.error('Failed to unlink', e);
      const errorCodeReceived = e?.errorCode || 'generic_error';

      trackAccountUnlinkingFailure(errorCodeReceived);

      setErrorCode(errorCodeReceived);
    }
  };

  const infoPanelSectionMessage = () => {
    let title = '';
    let message = '';
    if (!account?.isLinked) {
      title = 'Add a primary account';
      message =
        "Having a primary account ensures you retain access to your profile and achievements, even if you move companies. Your primary account must be an account you own and manage yourself - it can't be managed by your company.";
    } else if (isLoggedInAccountNotPrimary) {
      title = '';
      message = 'Log in to your primary account to link and unlink accounts';
    }
    if (!message) {
      return null;
    }
    return (
      <SectionMessage title={title}>
        <p>{message}</p>
      </SectionMessage>
    );
  };

  return (
    <div css={parentContentContainer}>
      <div css={containerStyles}>
        <div css={mainPanelStyles}>
          <Heading level={1}>{'Settings'}</Heading>
          <div css={descriptionContainer}>
            <Heading level={3}>{'Linked accounts'}</Heading>
            <div css={descriptionContentContainer}>
              <Text>
                {
                  'You can link multiple Atlassian accounts together to combine all of your Community achievements under one profile. Learn about '
                }
                <a
                  href="/forums/x/x/ba-p/2958563"
                  css={linkStyle}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {'linking accounts.'}
                </a>
              </Text>
            </div>
          </div>

          <div css={infoSectionContainer}>{infoPanelSectionMessage()}</div>
          <ul css={cardsContainer}>
            <li css={tempCardContainerWrapperStyes} key={account?.aaid}>
              <UnifiedAccountContainerCard>
                <UnifiedAccountCardContent
                  email={truncateString(
                    (account?.emailId || account?.aaid) as string,
                  )}
                  tags={getTaggedComponents(
                    !!account?.isPrimary,
                    true,
                    !!account?.isLinked,
                  )}
                  // change primary account temporarily disabled. (logged in to primary thus has no options, so disabling kebab menu)
                  // also, doesn't make sense to allow unlinking on an account that isn't linked
                  disableMenu={
                    !isLoggedInAccountNotPrimary || !account.isLinked
                  }
                  actions={linkedAccountDropdownActions(
                    {
                      isPrimaryAccount: account?.isPrimary ?? false,
                      isOrgOwned: account?.isManaged ?? true,
                      isLoggedInAccountNotPrimary: isLoggedInAccountNotPrimary,
                    },
                    {
                      unlinkAccount: async () => {
                        await handleUnlink(account?.aaid as string);
                      },
                      changePrimary: () => {
                        return;
                      },
                      makePrimary: async () => {
                        trackMakePrimaryAccountButtonClick();

                        try {
                          await setPrimaryAccount(account?.aaid as string);

                          trackMakePrimaryAccountSuccess();
                        } catch (e: any) {
                          console.error('Failed to make primary', e);
                          const errorCodeReceived =
                            e?.errorCode || 'generic_error';

                          trackMakePrimaryAccountFailure(errorCodeReceived);

                          setErrorCode(errorCodeReceived);
                        }
                      },
                    },
                  )}
                  data-testid={`account-card-${
                    account?.emailId || account?.aaid
                  }`}
                />
              </UnifiedAccountContainerCard>
            </li>
            {account?.isLinked
              ? account.linkedAccounts?.edges?.map((edge) => {
                  const { aaid, emailId, isPrimary, isManaged, isLinked } =
                    edge?.node ?? {};

                  return (
                    <li css={tempCardContainerWrapperStyes} key={aaid}>
                      <UnifiedAccountContainerCard>
                        <UnifiedAccountCardContent
                          email={truncateString(emailId as string)}
                          tags={getTaggedComponents(
                            isPrimary as boolean,
                            false,
                            !!isLinked,
                          )}
                          disableMenu={isLoggedInAccountNotPrimary}
                          actions={linkedAccountDropdownActions(
                            {
                              isPrimaryAccount: isPrimary ?? false,
                              isOrgOwned: isManaged ?? true,
                            },
                            {
                              unlinkAccount: async () => {
                                await handleUnlink(aaid as string);
                              },
                              changePrimary: () => {
                                return;
                              },
                              makePrimary: async () => {
                                trackMakePrimaryAccountButtonClick();

                                try {
                                  await setPrimaryAccount(aaid as string);

                                  trackMakePrimaryAccountSuccess();
                                } catch (e: any) {
                                  console.error('Failed to make primary', e);
                                  const errorCodeReceived =
                                    e?.errorCode || 'generic_error';

                                  trackMakePrimaryAccountFailure(
                                    errorCodeReceived,
                                  );

                                  setErrorCode(errorCodeReceived);
                                }
                              },
                            },
                          )}
                          data-testid={`account-card-${emailId}`}
                        />
                      </UnifiedAccountContainerCard>
                    </li>
                  );
                })
              : null}
            <li>
              <LinkComponent
                loggedInAccountEmail={account?.emailId || ''}
                isLoggedInLinked={!!account?.isLinked}
                isLoggedInPrimary={!!account?.isPrimary}
                onFailure={handleLinkFailure}
              />
            </li>
          </ul>

          <div css={learningSettingsContainer}>
            <Heading level={3}>{'Learning'}</Heading>
            <div css={descriptionContentContainer}>
              <Text>
                {
                  'Looking for learning settings? You can access your billing details and training credits through your old learning profile. Learn '
                }
                <a
                  href="https://university.atlassian.com/training_credits/accounts/all"
                  css={linkStyle}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {'how to access learning settings.'}
                </a>
              </Text>
            </div>
          </div>
          <FlagList
            flags={flags}
            onDismiss={handleDismiss}
            linkedEmail="testEmail"
          />
        </div>
      </div>
    </div>
  );
};

export default Settings;

const parentContentContainer = css({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
});

const cardsContainer = css({
  display: 'flex',
  justifyContent: 'left',
  width: '100%',
  flexWrap: 'wrap',
  marginTop: '40px',
  gap: '24px',
  padding: '0',
  listStyle: 'none',
  '> li': {
    marginTop: '0',
  },
  [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
    {
      justifyContent: 'center',
      width: '720px',
    },
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    marginTop: token('space.400'),
    justifyContent: 'center',
    marginBottom: token('space.400'),
    width: '343px',
  },
});

const infoSectionContainer = css({
  marginTop: '32px',
});

const descriptionContentContainer = css({
  marginTop: '8px',
  width: '100%',
});

const descriptionContainer = css({
  marginTop: '44px',
});

const learningSettingsContainer = css({
  marginTop: '32px',
});

const containerStyles = css({
  display: 'flex',
  marginTop: '72px',
  width: '918px',
  [`@media (min-width: ${MOBILE.MAX_WIDTH}px) and (max-width: ${DESKTOP.MIN_WIDTH}px)`]:
    {
      width: '720px',
      marginTop: '24px',
    },
  [`@media (max-width: ${MOBILE.MAX_WIDTH}px)`]: {
    marginTop: '26px',
    width: '343px',
  },
});

const mainPanelStyles = css({
  width: '918px',
  flex: 1,
});

const linkStyle = css({
  color: token('color.text.brand'),
  textDecoration: 'none',
});

const tempCardContainerWrapperStyes = css({ position: 'relative' });
